import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import copilotStore from "Stores/copilotStore";
import MessageRow from "./MessageRow";
import NextSteps from "./NextSteps";

const PIN_THRESHOLD_PX = 80;

/** True when a scroll container is at (or within the threshold of) its bottom. */
export const isNearBottom = (el, threshold = PIN_THRESHOLD_PX) =>
  !!el && el.scrollHeight - el.scrollTop - el.clientHeight < threshold;

const ChatThread = observer(() => {
  const endRef = useRef(null);
  const rootRef = useRef(null);
  // Pinned = the user is at (or near) the bottom, so the thread follows the
  // stream. Scrolling up unpins: nothing yanks the viewport while reading —
  // a "jump to latest" pill offers the way back instead.
  const pinned = copilotStore.threadPinned;
  const { messages } = copilotStore;
  const hasMessages = messages.length > 0;

  // Follow the stream: re-render fires on every token delta (observer), and
  // this key changes with both new messages and growing text blocks.
  const lastMessage = messages.length ? messages[messages.length - 1] : null;
  const scrollKey = lastMessage
    ? `${messages.length}_${(lastMessage.blocks || []).reduce(
        (size, block) => size + (block.markdown ? block.markdown.length : 1),
        0
      )}`
    : "empty";

  // The overflow container is the parent (.copilotPage__thread) — observe its
  // scroll position to know whether the user has left the bottom.
  // Only the USER unpins. Our own scrollIntoView also fires "scroll", and when
  // the reply grows in the same frame (a table loading) that event read as
  // "left the bottom", so the thread stopped following and new cards landed
  // below the fold.
  useEffect(() => {
    const container = rootRef.current ? rootRef.current.parentElement : null;
    if (!container) return undefined;
    let userAt = 0;
    const intent = () => {
      userAt = Date.now();
    };
    const onScroll = () => {
      if (isNearBottom(container)) copilotStore.setThreadPinned(true);
      else if (Date.now() - userAt < 600) copilotStore.setThreadPinned(false);
    };
    const userEvents = ["wheel", "touchmove", "keydown", "pointerdown"];
    userEvents.forEach(e => container.addEventListener(e, intent, { passive: true }));
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      userEvents.forEach(e => container.removeEventListener(e, intent));
      container.removeEventListener("scroll", onScroll);
    };
  }, [hasMessages]);

  useEffect(() => {
    if (pinned && endRef.current) {
      // auto, not smooth: smooth-scrolling on every streamed token stutters
      endRef.current.scrollIntoView({ behavior: "auto", block: "end" });
    }
  }, [scrollKey, pinned]);

  // Replies also grow without a new token: cards load their data, widgets size
  // themselves, images arrive. Follow that growth too, but only while pinned, so
  // a reader who scrolled up is never pulled down.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      if (copilotStore.threadPinned && endRef.current) {
        endRef.current.scrollIntoView({ behavior: "auto", block: "end" });
      }
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, [hasMessages]);

  // The jump pill lives at the thread/composer boundary (Copilot.js) and
  // signals through the store.
  useEffect(() => {
    if (copilotStore.threadJumpRequest && endRef.current) {
      copilotStore.setThreadPinned(true);
      endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [copilotStore.threadJumpRequest]);

  // Rename in place: click the title, type, Enter saves, Esc cancels.
  // Declared ABOVE the early return: React counts hooks per render, and an
  // empty thread returning first made this the 4th hook on some renders and
  // absent on others -- "Should have a queue", and the whole route crashed.
  const [editing, setEditing] = useState(null);

  if (!messages.length) {
    return null; // hero layout is rendered by Copilot.js
  }

  // Blocks of the whole assistant run around index (consecutive assistant
  // messages), for turn-level state like the rail colour.
  const turnBlocksFor = index => {
    if (messages[index].role !== "assistant") return null;
    let start = index;
    while (start > 0 && messages[start - 1].role === "assistant") start -= 1;
    let end = index;
    while (end < messages.length - 1 && messages[end + 1].role === "assistant") end += 1;
    const out = [];
    for (let i = start; i <= end; i += 1) out.push(...(messages[i].blocks || []));
    return out;
  };
  // A decision is superseded once anything newer asks for input (or the user
  // typed): its unpicked buttons must not stay clickable up the thread.
  const DECISIONS = ["cta_chips", "connect_integration", "question_form", "quick_replies", "confirmation", "comparison_grid"];
  let lastDecisionAt = -1;
  messages.forEach((m, i) => {
    if (m.role === "user" || (m.blocks || []).some(b => DECISIONS.includes(b.type) && !b.hidden)) lastDecisionAt = i;
  });
  const firstUser = messages.find(m => m.role === "user");
  const userTurns = messages.filter(m => m.role === "user").length;
  const title = copilotStore.conversationTitle || (firstUser && firstUser.text) || "";
  const commitTitle = () => {
    if (editing !== null && editing.trim() && editing.trim() !== title) {
      copilotStore.renameConversation(copilotStore.conversationId, editing);
    }
    setEditing(null);
  };
  return (
    <div className="copilotThread" ref={rootRef}>
      <div className="copilotThread__inner">
        {/* Document header (styled on the full page only): the conversation
            reads as a titled brief, not an untitled scroll. */}
        {title && (
          <header className="copilotDocHead">
            {editing === null ? (
              <h1 className="copilotDocHead__title" title="Click to rename" onClick={() => setEditing(title)}>
                {title}
              </h1>
            ) : (
              <input
                className="copilotDocHead__titleInput"
                value={editing}
                autoFocus
                maxLength={120}
                onChange={e => setEditing(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={e => {
                  if (e.key === "Enter") commitTitle();
                  if (e.key === "Escape") setEditing(null);
                }}
              />
            )}
            <div className="copilotDocHead__meta">
              <span>{userTurns} {userTurns === 1 ? "turn" : "turns"}</span>
            </div>
          </header>
        )}
        {messages.map((message, index) => {
          // Rule a divider when the conversation moves to a different section.
          // Only user turns carry one -- the answer belongs to the question
          // above it, so a divider between them would split a single exchange.
          const previous = messages
            .slice(0, index)
            .reverse()
            .find(item => item.section);
          const moved =
            message.section && previous && previous.section !== message.section;
          return (
            <React.Fragment key={message.id}>
              {moved && (
                <div className="copilotThread__divider" role="separator">
                  <span>{message.sectionLabel || message.section}</span>
                </div>
              )}
              <MessageRow
                message={message}
                continued={
                  message.role === "assistant" &&
                  index > 0 &&
                  messages[index - 1].role === "assistant" &&
                  !moved
                }
                // One action row per turn: only the last assistant message
                // in a run of them carries it.
                endOfTurn={
                  message.role === "assistant" &&
                  (index === messages.length - 1 || messages[index + 1].role !== "assistant")
                }
                prevUserText={
                  (messages.slice(0, index).reverse().find(m => m.role === "user") || {}).text || ""
                }
                isLast={index === messages.length - 1}
                superseded={index < lastDecisionAt}
                turnBlocks={turnBlocksFor(index)}
              />
            </React.Fragment>
          );
        })}
        <NextSteps />
        <div ref={endRef} />
      </div>
    </div>
  );
});

export default ChatThread;
